import {
	DestroyRef,
	Directive,
	EmbeddedViewRef,
	inject,
	NgModule,
	OnInit,
	TemplateRef,
	ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ClientScreen, ClientService } from '../services/client';

interface ClientScreenContext {
	isDesktop: boolean;
	isMobile: boolean;
	screen: ClientScreen;
}

@Directive({
	selector: '[clientScreen]',
	standalone: true,
})
export class ClientScreenDirective implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly templateRef = inject(TemplateRef<ClientScreenContext>);
	private readonly viewContainer = inject(ViewContainerRef);
	private readonly clientService = inject(ClientService);

	constructor() {
		this.viewContainer.createEmbeddedView(this.templateRef, this.context);
	}

	ngOnInit(): void {
		this.clientService.screen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			const viewRef = this.viewContainer.get(0) as EmbeddedViewRef<ClientScreenContext>;
			viewRef.context = this.context;
			viewRef.detectChanges();
		});
	}

	private get context(): ClientScreenContext {
		const screen = ClientService.screen;

		return {
			isDesktop: screen === ClientScreen.DESKTOP,
			isMobile: screen === ClientScreen.MOBILE,
			screen,
		};
	}
}

@Directive({
	selector: '[ifGivenScreen]',
	standalone: true,
})
export abstract class IfGivenScreenDirective implements OnInit {
	protected abstract givenScreen: ClientScreen;

	private readonly destroyRef = inject(DestroyRef);
	private readonly templateRef = inject(TemplateRef<ClientScreenContext>);
	private readonly viewContainer = inject(ViewContainerRef);
	private readonly clientService = inject(ClientService);

	private rendered = false;

	ngOnInit(): void {
		this.clientService.screen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.afterScreenChanged();
		});
	}

	private afterScreenChanged(): void {
		const screen = ClientService.screen;
		const shouldBeVisible = screen === this.givenScreen;

		if (!shouldBeVisible) {
			this.viewContainer.clear();
			this.rendered = false;
		} else if (!this.rendered) {
			this.viewContainer.createEmbeddedView(this.templateRef);
			this.rendered = true;
		}
	}
}

@Directive({
	selector: '[ifDesktop]',
	standalone: true,
})
export class IfDesktopDirective extends IfGivenScreenDirective {
	protected givenScreen = ClientScreen.DESKTOP;
}

@Directive({
	selector: '[ifMobile]',
	standalone: true,
})
export class IfMobileDirective extends IfGivenScreenDirective {
	protected givenScreen = ClientScreen.MOBILE;
}

@NgModule({
	imports: [ClientScreenDirective, IfDesktopDirective, IfMobileDirective],
	exports: [ClientScreenDirective, IfDesktopDirective, IfMobileDirective],
})
export class ClientScreenDirectives {}
